import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';
import { CrossTabCommunicationService } from './cross-tab-communication.service';
import { AccessTokenPayload } from '@shared/models/auth/auth.models';
import {
  SignInUserData,
  UserGroup,
  UserStatus,
} from '@features/authentication/models/authentication.model';
import { AuthenticationApiService } from '@features/authentication/services/authentication-api.service';

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  userData: SignInUserData | null;
}

/**
 * Session state for the learner platform: token storage, JWT expiry checks, and
 * refresh. Components read `authState()`; the auth interceptor is the only thing
 * that puts a token on a request.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly ACCOUNT_ID_KEY = 'accountId';
  private readonly GROUP_KEY = 'group';
  private readonly USER_DATA_KEY = 'userData';
  private readonly USER_SETTINGS_KEY = 'userSettings';
  private readonly PASSWORD_CHANGE_REQUIRED_KEY = 'passwordChangeRequired';
  private readonly SIGN_OUT_REASON_KEY = 'signOutReason';

  private readonly router = inject(Router);
  private readonly storageService = inject(StorageService);
  private readonly platformService = inject(PlatformService);
  private readonly crossTabCommunication = inject(CrossTabCommunicationService);
  private readonly authenticationApiService = inject(AuthenticationApiService);

  public readonly authState = signal<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    userData: null,
  });

  constructor() {
    this.initializeAuthState();
  }

  /** Rehydrate the session from storage on app start. */
  private initializeAuthState(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    const accessToken = this.storageService.getItem(this.ACCESS_TOKEN_KEY);
    const refreshToken = this.storageService.getItem(this.REFRESH_TOKEN_KEY);
    if (!accessToken) {
      return;
    }

    if (this.isValid(accessToken)) {
      this.authState.set({
        isAuthenticated: true,
        accessToken,
        refreshToken,
        userData: this.readStoredUserData(accessToken, refreshToken),
      });
      return;
    }

    // Expired on load — the interceptor will retry through refreshAccessToken()
    // on the first request, so failing quietly here is enough.
    if (refreshToken) {
      void this.refreshAccessToken();
    }
  }

  private readStoredUserData(
    accessToken: string,
    refreshToken: string | null
  ): SignInUserData | null {
    const userDataStr = this.storageService.getItem(this.USER_DATA_KEY);
    const userSettingsStr = this.storageService.getItem(this.USER_SETTINGS_KEY);
    const accountId = this.storageService.getItem(this.ACCOUNT_ID_KEY);
    const group = this.storageService.getItem(this.GROUP_KEY);

    if (!userDataStr || !userSettingsStr || !accountId || !group) {
      return null;
    }

    try {
      return {
        accountId,
        status: UserStatus.active,
        group: group as UserGroup,
        passwordChangeRequired:
          this.storageService.getItem(this.PASSWORD_CHANGE_REQUIRED_KEY) === 'true',
        accessToken,
        refreshToken: refreshToken || '',
        userData: JSON.parse(userDataStr),
        userSettings: JSON.parse(userSettingsStr),
      };
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  /**
   * Return an Authorization header, refreshing the access token first when it has
   * expired. Returns null when there is no way to produce a valid token.
   */
  public async getBearer(): Promise<{ Authorization: string } | null> {
    const accessToken = this.storageService.getItem(this.ACCESS_TOKEN_KEY);
    if (this.isValid(accessToken)) {
      return { Authorization: `Bearer ${accessToken}` };
    }

    if (!this.storageService.getItem(this.REFRESH_TOKEN_KEY)) {
      return null;
    }

    const newAccessToken = await this.refreshAccessToken();
    return newAccessToken ? { Authorization: `Bearer ${newAccessToken}` } : null;
  }

  /**
   * Exchange the refresh token for a new access token. Resolves to null on any
   * failure — navigation on failure is the interceptor's job, not this method's.
   */
  public async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.storageService.getItem(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return null;
    }

    const response = await firstValueFrom(
      this.authenticationApiService.refreshToken(refreshToken).pipe(catchError(() => of(null)))
    );

    if (typeof response !== 'string' || !response) {
      return null;
    }

    // Keep the refreshed token in whichever store the session already lives in.
    const isSessionScoped = this.storageService.isSessionScoped(this.REFRESH_TOKEN_KEY);
    this.storageService.setItem(this.ACCESS_TOKEN_KEY, response, isSessionScoped);
    this.authState.set({ ...this.authState(), accessToken: response });
    return response;
  }

  /**
   * @param remember Persist the session across browser restarts. When false the
   *   session lives in tab-scoped storage and disappears when the tab closes,
   *   which is what an unchecked "remember me" has to mean.
   */
  public setAuthData(userData: SignInUserData, remember = true): void {
    const session = !remember;
    this.storageService.setItem(this.ACCESS_TOKEN_KEY, userData.accessToken, session);
    this.storageService.setItem(this.REFRESH_TOKEN_KEY, userData.refreshToken, session);
    this.storageService.setItem(this.ACCOUNT_ID_KEY, String(userData.accountId), session);
    this.storageService.setItem(this.GROUP_KEY, String(userData.group), session);
    this.storageService.setItem(this.USER_DATA_KEY, JSON.stringify(userData.userData), session);
    this.storageService.setItem(
      this.USER_SETTINGS_KEY,
      JSON.stringify(userData.userSettings),
      session
    );
    this.storageService.setItem(
      this.PASSWORD_CHANGE_REQUIRED_KEY,
      String(userData.passwordChangeRequired === true),
      session
    );

    this.authState.set({
      isAuthenticated: true,
      accessToken: userData.accessToken,
      refreshToken: userData.refreshToken,
      userData,
    });

    this.crossTabCommunication.broadcastAuthStateChange(true, undefined);
  }

  /**
   * Clear the session and send the user to the sign-out screen.
   *
   * @param reason Where the sign-out came from (file:line), kept for support triage.
   * @param returnUrl Route to come back to after signing in again.
   */
  /**
   * Record a new portrait for the signed-in learner.
   *
   * The user record is written once at sign-in and there is no endpoint that
   * re-reads it, so a photo uploaded mid-session would otherwise not appear
   * until the next authentication. Both media types are set: `main` is what the
   * toolbar avatar reads, `card_photo` what the settings sheet prefers, and the
   * upload stores the same image under both.
   *
   * Storage is updated as well as the signal — a reload rehydrates from there.
   */
  public updateOwnMedia(url: string): void {
    const state = this.authState();
    const data = state.userData;
    if (!data?.userData) {
      return;
    }

    const others = (data.userData.media ?? []).filter(
      item => item.type !== 'main' && item.type !== 'card_photo'
    );
    const media = [
      ...others,
      { ...(data.userData.media ?? []).find(m => m.type === 'main'), type: 'main', url },
      { ...(data.userData.media ?? []).find(m => m.type === 'card_photo'), type: 'card_photo', url },
    ] as typeof data.userData.media;

    const userData = { ...data.userData, media };
    this.storageService.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
    this.authState.set({ ...state, userData: { ...data, userData } });
  }

  /**
   * Record edited contact details for the signed-in learner.
   *
   * Same reason as {@link updateOwnMedia}: the user record is written once at
   * sign-in and never re-read, so a save would not survive reopening the sheet.
   */
  public updateOwnProfile(fields: {
    firstname: string;
    lastname: string;
    dateOfBirth?: string;
    phoneNumber: string;
    email: string;
  }): void {
    const state = this.authState();
    const data = state.userData;
    if (!data?.userData) {
      return;
    }

    const userData = { ...data.userData, ...fields };
    this.storageService.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
    this.authState.set({ ...state, userData: { ...data, userData } });
  }

  /**
   * Record the account's language and colour scheme for this session.
   *
   * Sign-in applies `userSettings` from the server, so the stored copy has to
   * follow a change too — otherwise a reload would restore the old preference
   * from storage and undo what the learner just picked.
   */
  public updateOwnSettings(settings: { localization?: string; appearance?: string }): void {
    const state = this.authState();
    const data = state.userData;
    if (!data) {
      return;
    }

    const userSettings = { ...data.userSettings, ...settings } as typeof data.userSettings;
    this.storageService.setItem(this.USER_SETTINGS_KEY, JSON.stringify(userSettings));
    this.authState.set({ ...state, userData: { ...data, userSettings } });
  }

  public signOut(reason: string, returnUrl: string): void {
    [
      this.ACCESS_TOKEN_KEY,
      this.REFRESH_TOKEN_KEY,
      this.ACCOUNT_ID_KEY,
      this.GROUP_KEY,
      this.USER_DATA_KEY,
      this.USER_SETTINGS_KEY,
      this.PASSWORD_CHANGE_REQUIRED_KEY,
    ].forEach(key => this.storageService.removeItem(key));

    this.storageService.setItem(this.SIGN_OUT_REASON_KEY, reason);

    this.authState.set({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      userData: null,
    });

    this.crossTabCommunication.broadcastSignOut();

    this.router.navigate(['/authentication/sign-out'], { queryParams: { returnUrl, reason } });
  }

  public getSignOutReason(): string | null {
    return this.storageService.getItem(this.SIGN_OUT_REASON_KEY);
  }

  public clearSignOutReason(): void {
    this.storageService.removeItem(this.SIGN_OUT_REASON_KEY);
  }

  public getAccessToken(): string | null {
    return this.authState().accessToken || this.storageService.getItem(this.ACCESS_TOKEN_KEY);
  }

  public isAuthenticated(): boolean {
    return this.authState().isAuthenticated;
  }

  public getUserData(): SignInUserData | null {
    return this.authState().userData;
  }

  /** True when the JWT parses and has not expired. */
  private isValid(token: string | null): boolean {
    const payload = this.decodeAccessTokenPayload(token);
    return !!payload?.exp && payload.exp > Math.floor(Date.now() / 1000);
  }

  private decodeAccessTokenPayload(token: string | null): AccessTokenPayload | null {
    if (!token) {
      return null;
    }

    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) {
        return null;
      }
      const jsonPayload = decodeURIComponent(
        atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map(character => '%' + ('00' + character.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload) as AccessTokenPayload;
    } catch {
      return null;
    }
  }
}
